import axios from "axios";
import { API_BASEURL } from "../utils/baseURL";
import { authHeader } from "../utils/headers";

export type UserRole = "Administrator" | "Manager" | "User";
export type UserStatus = "Active" | "Suspended";
export type UserSortField =
  | "displayName"
  | "email"
  | "role"
  | "status"
  | "directGrantCount";
export type SortDirection = "asc" | "desc";

export interface FolderGrantDto {
  folderId: string;
  folderName: string;
  grantedDate?: string;
}

export interface ApplicationUserListItem {
  tenantId: string;
  objectId: string;
  displayName: string;
  email: string;
  role: UserRole | string;
  status: UserStatus | string;
  createdDate: string;
  directGrantCount: number;
  directGrants?: FolderGrantDto[];
  accessibleFolders?: FolderGrantDto[];
}

export interface ListApplicationUsersRequest {
  page: number;
  pageSize: number;
  roles: UserRole[];
  statuses: UserStatus[];
  sort: {
    field: UserSortField;
    direction: SortDirection;
  };
}

export interface ChangeRoleRequest {
  role: UserRole | string;
}

interface ChangeStatusRequest {
  status: UserStatus | string;
}

export interface AddFolderGrantRequest {
  folderId: string;
}

export interface AdminFolderDto {
  id: string;
  name: string;
  hasChildren?: boolean;
}

const serializeUserQuery = (request: ListApplicationUsersRequest) => {
  const params = new URLSearchParams();

  params.set("page", String(request.page));
  params.set("pageSize", String(request.pageSize));
  params.set("roles", request.roles.join(","));
  params.set("statuses", request.statuses.join(","));
  params.set("sortBy", request.sort.field);
  params.set("sortDirection", request.sort.direction);

  return params.toString();
};

const normalizeFolders = (
  data: AdminFolderDto[] | { folders?: AdminFolderDto[] },
) => (Array.isArray(data) ? data : (data.folders ?? []));

const normalizeAxiosError = (error: any) => error.response ?? error;

export const listApplicationUsers = async (
  request: ListApplicationUsersRequest,
): Promise<{ status: number; data: any; response?: any; code?: string }> =>
  await axios
    .get(`${API_BASEURL}v1/admin/users?${serializeUserQuery(request)}`, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const getApplicationUser = async (
  oid: string,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .get(`${API_BASEURL}v1/admin/users/${oid}`, { headers: authHeader() })
    .catch(normalizeAxiosError);

export const changeApplicationUserRole = async (
  oid: string,
  request: ChangeRoleRequest,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .put(`${API_BASEURL}v1/admin/users/${oid}/role`, request, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const changeApplicationUserStatus = async (
  oid: string,
  request: ChangeStatusRequest,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .put(`${API_BASEURL}v1/admin/users/${oid}/status`, request, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const removeApplicationUser = async (
  oid: string,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .delete(`${API_BASEURL}v1/admin/users/${oid}`, { headers: authHeader() })
    .catch(normalizeAxiosError);

export const listApplicationUserGrants = async (
  oid: string,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .get(`${API_BASEURL}v1/admin/users/${oid}/grants`, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const addApplicationUserGrant = async (
  oid: string,
  request: AddFolderGrantRequest,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .post(`${API_BASEURL}v1/admin/users/${oid}/grants`, request, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const removeApplicationUserGrant = async (
  oid: string,
  folderId: string,
): Promise<{ status: number; data: any; response?: any }> =>
  await axios
    .delete(`${API_BASEURL}v1/admin/users/${oid}/grants/${folderId}`, {
      headers: authHeader(),
    })
    .catch(normalizeAxiosError);

export const listAdminFolderRoots = async (): Promise<{
  status: number;
  data: AdminFolderDto[];
  response?: any;
}> => {
  const response = await axios
    .get(`${API_BASEURL}v1/admin/folders`, { headers: authHeader() })
    .catch(normalizeAxiosError);

  return response.status === 200
    ? { ...response, data: normalizeFolders(response.data) }
    : response;
};

export const listAdminFolderChildren = async (
  parentId: string,
): Promise<{ status: number; data: AdminFolderDto[]; response?: any }> => {
  const response = await axios
    .get(
      `${API_BASEURL}v1/admin/folders?parentId=${encodeURIComponent(parentId)}`,
      {
        headers: authHeader(),
      },
    )
    .catch(normalizeAxiosError);

  return response.status === 200
    ? { ...response, data: normalizeFolders(response.data) }
    : response;
};
